﻿import { autorun } from "mobx";
import { Extent } from "ol/extent";
import Feature from "ol/Feature";
import Polygon from "ol/geom/Polygon";
import LayerGroup, { Options } from "ol/layer/Group";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import OSM from 'ol/source/OSM';
import TileSource from "ol/source/Tile";
import VectorSource from "ol/source/Vector";
import XYZSource from "ol/source/XYZ";
import FillStyle from "ol/style/Fill";
import Style from "ol/style/Style";
import { IChartConfig } from "../models/ChartModels";
import { ChartStates } from "../models/ChartStates";
import { MobxReactionDisposer } from "../models/MobxReactionDisposer";
import DepthLayer from "./ChartSubLayers/DepthLayer";
import DepthLayerBase from "./ChartSubLayers/DepthLayerBase";
import ExternalLayer from "./ChartSubLayers/ExternalLayer";
import MsgBaseLayer from './ChartSubLayers/MsgBaseLayer';
import MsgBaseSource from './ChartSubLayers/MsgBaseSource';
import MsgWmsLayer from "./ChartSubLayers/MsgWmsLayer";
import MsgWmsSource from "./ChartSubLayers/MsgWmsSource";
import OsmLayer from "./ChartSubLayers/OsmLayer";
import OsmVectorLayer, { OsmVectorLayerOptions } from "./ChartSubLayers/OsmVectorLayer";
import WorldImageryLayer from "./ChartSubLayers/WorldImageryLayer";

export interface ChartLayerOptions extends Options {
    extent: Extent
}

export interface ChartSubLayers {
    wiLayer?: TileLayer<TileSource>;
    osmLayer?: TileLayer<TileSource>;
    msgBaseLayer?: TileLayer<TileSource>;
    msgWmsLayer?: TileLayer<TileSource>;
    depthLayer?: DepthLayerBase;
}

class ChartLayer extends LayerGroup {

    public readonly wiLayer: TileLayer<TileSource>;
    public readonly osmLayer?: TileLayer<TileSource>;
    public readonly osmVectorLayer?: OsmVectorLayer;
    public readonly msgBaseLayer: TileLayer<TileSource>;
    public readonly msgWmsLayer: TileLayer<TileSource>;
    public readonly depthLayer: DepthLayerBase;
    public readonly externalLayer: TileLayer<TileSource>;

    protected mobxDisposer = new MobxReactionDisposer();
    private backgroundFeature: Feature;   

    constructor(
        config: IChartConfig,
        protected states: ChartStates,
        protected options: ChartLayerOptions,
        osmVectorLayerOptions?: OsmVectorLayerOptions,
        opt_sublayers?: ChartSubLayers,
        externalLayerOptions?: ChartLayerOptions) {

        super(options);

        const layers = this.getLayers();
        const extent = this.options.extent;

        this.wiLayer = opt_sublayers?.wiLayer ??
            new WorldImageryLayer(states, {
                source: new XYZSource(config.satelliteLayer),
                extent
            });
        layers.push(this.wiLayer);

        if (config.osmVectorLayer?.url && osmVectorLayerOptions) {
            this.osmVectorLayer = new OsmVectorLayer(states, config.osmVectorLayer.url, osmVectorLayerOptions);
            layers.push(this.osmVectorLayer);
        }
        else if (config.osmLayer?.url) {
            this.osmLayer = new OsmLayer(states, {
                source: new OSM(config.osmLayer),
                extent
            });
            layers.push(this.osmLayer);
        }

        this.msgBaseLayer = opt_sublayers?.msgBaseLayer ??
            new MsgBaseLayer(states, {
                source: new MsgBaseSource(config, states),
                extent
            });
        layers.push(this.msgBaseLayer);

        this.msgWmsLayer = opt_sublayers?.msgBaseLayer ??
            new MsgWmsLayer(states, {
                source: new MsgWmsSource(config, states),
                extent
            });
        layers.push(this.msgWmsLayer);

        this.externalLayer = new ExternalLayer(states, {
            ...(externalLayerOptions ?? {}),
            source: undefined,
            extent,
        });
        layers.push(this.externalLayer);

        this.depthLayer = opt_sublayers?.depthLayer ??
            new DepthLayer(states, {
                source: new XYZSource({
                    minZoom: 1,
                    maxZoom: 13,
                    transition: 0,
                    crossOrigin: "anonymous",
                }),
                extent
            }, config.chartLayer?.url);
        layers.push(this.depthLayer);

        this.backgroundFeature = new Feature(new Polygon([[
            [this.options.extent[0], this.options.extent[1]],
            [this.options.extent[0], this.options.extent[3]],
            [this.options.extent[2], this.options.extent[3]],
            [this.options.extent[2], this.options.extent[1]],
            [this.options.extent[0], this.options.extent[1]]
        ]]));

        layers.push(new VectorLayer({
            updateWhileAnimating: true,
            updateWhileInteracting: true,
            source: new VectorSource({
                wrapX: false,
                features: [this.backgroundFeature]
            })
        }));

        this.mobxDisposer.add([
            autorun(() => {
                this.setBrightness(states.chart.brightness ?? 0);
            })
        ]);
    }

    public dispose() {
        this.mobxDisposer.clear();
        this.getLayers().forEach(l => l.dispose());
        super.dispose();
    }

    private setBrightness(brightness: number) {
        if (brightness === 0) {
            this.backgroundFeature.setStyle(new Style());
        } else {
            this.backgroundFeature.setStyle(new Style({
                fill: new FillStyle({
                    color: `rgba(${brightness < 0 ? "0, 0, 0" : "255, 255, 255"}, ${Math.abs(brightness)})`
                })
            }));
        }
    }
}

export default ChartLayer;